import type { ShoppingCartDTO } from '@t/api.types';
import type { SelectedAddOn, SelectedEquipment } from '@t/configuration.types';
import type { PlanAddOn } from '@t/productOffering.types';

export interface InitialSelections {
  equipment?: SelectedEquipment;
  addOns?: SelectedAddOn[];
}

export const getInitialSelections = (
  cart: ShoppingCartDTO | null,
  planId: string,
  planAddOns?: PlanAddOn[],
  equipmentIds?: Set<string>
): InitialSelections | undefined => {
  const planItem = cart?.cartItem?.find((item) => item.productOffering?.id === planId);
  if (!planItem?.childItems?.length) return undefined;

  // Build id→code mapping from plan add-ons. The cart stores productOffering.id
  // (= PlanAddOn.id) but the UI selection system uses PlanAddOn.code.
  const idToCode = new Map(planAddOns?.map((a) => [a.id, a.code]));

  // Identify equipment: explicit type flag OR positive match against known equipment IDs
  const equipmentChild = planItem.childItems.find((child) => {
    if (child.type === 'equipment') return true;
    const id = child.productOffering?.id;
    return id != null && equipmentIds != null && equipmentIds.has(id);
  });
  // The backend stores equipment as a single row carrying its own count, so read the
  // child's quantity directly rather than assuming per-unit split rows (SC-12443).
  const equipmentOfferingId = equipmentChild?.productOffering?.id;
  const equipment: SelectedEquipment | undefined = equipmentOfferingId
    ? { id: equipmentOfferingId, quantity: equipmentChild?.quantity ?? 1 }
    : undefined;

  // Only count children that map to known plan add-ons
  const addOnChildren = planItem.childItems.filter(
    (child) => child.productOffering?.id && idToCode.has(child.productOffering.id)
  );

  // Group add-on children by offering ID and sum quantities,
  // since the cart creates separate items for each unit.
  const addOnMap = new Map<string, number>();
  for (const child of addOnChildren) {
    const offeringId = child.productOffering!.id!;
    const code = idToCode.get(offeringId) ?? offeringId;
    addOnMap.set(code, (addOnMap.get(code) ?? 0) + (child.quantity ?? 1));
  }

  return {
    equipment,
    addOns: addOnMap.size > 0 ? [...addOnMap].map(([code, quantity]) => ({ code, quantity })) : undefined,
  };
};
